/* eslint-disable no-case-declarations */
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { StrophePlugin } from './strophe-plugin';
import { JingleSession } from './strophe.jingle.session';
import { Subject } from "rxjs";
import { $iq, Strophe } from 'strophe.js';

interface JingleIceConfig {
    iceServers: any[];
    iceTransportPolicy?: 'all' | 'relay';
}

export class StropheJinglePlugin extends StrophePlugin {

    public sessions: Record<string, JingleSession> = {};
    private connectionRef: any = null;
    public ice_config: JingleIceConfig = { iceServers: [] };
    public company_config: Record<string, any> = {};
    private pc_constraints: Record<string, any> = { rtcpMuxPolicy: 'require', bundlePolicy: 'max-bundle' };
    public media_constraints: Record<string, any> = {};
    private jingleIqTimeout = 15000;
    private logger: LoggerService = LoggerService.getInstance();

    public rxSubject = new Subject<any>();

    public constructor() { super('jingle'); }

    public static build() { return new StropheJinglePlugin(); }

    public init(connection: any): void {
        super.init(connection);
        this.connection = connection;
        if (this.connection.disco) {
            // http://xmpp.org/extensions/xep-0167.html#support
            // http://xmpp.org/extensions/xep-0176.html#support
            this.connection.disco.addFeature('urn:xmpp:jingle:1');
            this.connection.disco.addFeature('urn:xmpp:jingle:apps:rtp:1');
            this.connection.disco.addFeature('urn:xmpp:jingle:transports:ice-udp:1');
            this.connection.disco.addFeature('urn:xmpp:jingle:apps:rtp:audio');
            this.connection.disco.addFeature('urn:xmpp:jingle:apps:rtp:video');
            // this is dealt with by SDP O/A so we don't need to annouce this
            // this.connection.disco.addFeature('urn:xmpp:jingle:apps:rtp:rtcp-fb:0'); // XEP-0293
            // this.connection.disco.addFeature('urn:xmpp:jingle:apps:rtp:rtp-hdrext:0'); // XEP-0294
            this.connection.disco.addFeature('urn:ietf:rfc:5761'); // rtcp-mux
            // this.connection.disco.addFeature('urn:ietf:rfc:5888'); // a=group, e.g. bundle
            // this.connection.disco.addFeature('urn:ietf:rfc:5576'); // a=ssrc
        }

        // Handle timeout
        this.jingleIqTimeout = (config && config.jingleIqTimeout) ? config.jingleIqTimeout : 15_000;
        this.attachHandlers();

        for (const key in this.sessions) {
            if (Object.prototype.hasOwnProperty.call(this.sessions, key)) this.sessions[key].connection = connection;
        }
    }

    public attachHandlers() {
        if (this.connectionRef) { this.connection.deleteHandler(this.connectionRef); this.connectionRef = null; }
        this.connectionRef = this.connection.addHandler((stanza: any) => { this.onJingle(stanza); return true; }, 'urn:xmpp:jingle:1', 'iq', 'set');
    }

    public async initiate(peerJid: string, myJid: string, localType: string, streams: MediaStream[], sid: string, confId: string, phoneNumber: string, forceTurn?: boolean, isEchoTest?: boolean, otherSid: string = "", callShift: boolean = false): Promise<JingleSession> {
        const id = sid ? sid : `web_${Math.random().toString(36).slice(2, 12)}`;
        const session = new JingleSession(myJid || this.connection.jid, id, this.connection, this.jingleIqTimeout);

        // Handle streams
        streams?.forEach((stream: MediaStream) => { session.localStreams.push(stream); });

        // Handle iceConfig
        this.ice_config.iceTransportPolicy = forceTurn ? "relay" : "all";
        this.updateSessionData(session);

        session.replaceSid = otherSid;
        session.callShift = callShift;

        if (isEchoTest) session.isEchoTest = true;
        if (localType) session.localType = localType;
        if (confId !== undefined) session.conference_id = confId;
        session.calledNumber = (phoneNumber && phoneNumber !== "") ? phoneNumber : null;

        this.sessions[session.sid] = session;

        //send offer only when session is fully initiated
        await session.initiate(peerJid, true);

        session.sendOffer(true);

        return session;
    }

    public async initiateDataChannelSession(peerJid: string, myJid: string, confId?: string): Promise<JingleSession> {
        const id = `web_${Math.random().toString(36).slice(2, 12)}`;
        const session = new JingleSession(myJid, id, this.connection, this.jingleIqTimeout);
        session.localType = 'application';
        if (confId) session.conference_id = confId;
        this.ice_config.iceTransportPolicy = 'all';
        this.updateSessionData(session);
        await session.initiate(peerJid, true);

        this.sessions[session.sid] = session;

        return session;
    }

    public async initiateBundleSession(peerJid: string, myJid: string, sid: string, stream: MediaStream, conferenceId: string): Promise<JingleSession> {
        const session = new JingleSession(myJid, sid, this.connection, this.jingleIqTimeout);

        session.localType = 'audio+video';
        session.conference_id = conferenceId;
        session.localStreams.push(stream);

        this.ice_config.iceTransportPolicy = 'all';
        this.updateSessionData(session);

        await session.initiate(peerJid, true);
        this.sessions[session.sid] = session;
        session.sendOffer(true);
        return session;
    }

    public terminateAllSessions(reason?: string, text?: string) {
        //terminate all sessions
        for (const id in this.sessions) {
            if (this.sessions[id]) {
                this.sessions[id].sendTerminate(reason || ((!this.sessions[id].active()) ? 'cancel' : null), text);
                this.sessions[id].terminate();
                delete this.sessions[id];
            }
        }
    }

    public terminate(sid: string, reason?: string, text?: string): void {
        if (sid && this.sessions[sid]) {
            //terminate the session by SID
            if (this.sessions[sid].state !== 'ended') {
                this.sessions[sid].sendTerminate(reason || ((!this.sessions[sid].active()) ? 'cancel' : null), text);
                this.sessions[sid].terminate();
            }
            delete this.sessions[sid];
        }
    }

    private updateSessionData(session: any, remoteType?: any): void {
        session.media_constraints = this.media_constraints;
        session.pc_constraints = this.pc_constraints;
        session.ice_config = this.ice_config;
        session.company_config = this.company_config;
        session.logger = this.logger;
        if (remoteType) session.remoteType = remoteType;
    }

    private onJingle(iq: any): boolean {
        try {
            const iqElem = $(iq);
            const from = iqElem.attr('from');
            const to = iqElem.attr('to');
            const jingleElem = iqElem.find('>jingle');
            const sid = jingleElem.attr('sid');
            const action = jingleElem.attr('action');
            const remoteType = jingleElem.attr('localType');

            let sess: JingleSession = this.sessions[sid];

            // Send ack first
            const ack = $iq({ type: 'result', to: from, id: iq.getAttribute('id') });

            if (action !== 'session-initiate') {
                if (!sess) {
                    //ack.type = 'error';
                    ack.c('error', { type: 'cancel' })
                        .c('item-not-found', { xmlns: 'urn:ietf:params:xml:ns:xmpp-stanzas' }).up()
                        .c('unknown-session', { xmlns: 'urn:xmpp:jingle:errors:1' });
                    this.connection.send(ack);
                    this.rxSubject.next({ name: 'jingleSessionTerminate', data: { sid: sid } });
                    return true;
                }
                // Compare from to sess.peerjid (bare jid comparison for later compat with message-mode)
                // local jid is not checked
                if (Strophe.getBareJidFromJid(from) !== Strophe.getBareJidFromJid(sess.peerjid)) {
                    this.logger.warn('[Strophe.jingle] jid mismatch for session id', sid, from, sess.peerjid);
                    //ack.type = 'error';
                    ack.c('error', { type: 'cancel' })
                        .c('item-not-found', { xmlns: 'urn:ietf:params:xml:ns:xmpp-stanzas' }).up()
                        .c('unknown-session', { xmlns: 'urn:xmpp:jingle:errors:1' });
                    this.connection.send(ack);
                    return true;
                }
            }
            else if (action === 'session-initiate' && sess) {
                // Existing session with same session id
                // This might be out-of-order if the sess.peerjid is the same as from
                //ack.type = 'error';
                ack.c('error', { type: 'cancel' })
                    .c('service-unavailable', { xmlns: 'urn:ietf:params:xml:ns:xmpp-stanzas' }).up();
                this.logger.warn('[Strophe.jingle] duplicate session id', sid);
                this.connection.send(ack);
                return true;
            }

            // FIXME: check for a defined action
            if (action !== "transport-replace") this.connection.send(ack);

            // Handle jingle action events
            switch (action) {
                case 'session-initiate':
                    this.logger.debug(`[StropheJinglePlugin] session-initiate (sid: ${sid}, remoteType: ${remoteType})`);

                    sess = new JingleSession(to, sid, this.connection, this.jingleIqTimeout);
                    this.updateSessionData(sess, remoteType);

                    const conference_id = $(iq).find('conference').attr("id");
                    if (conference_id) { sess.conference_id = conference_id; }

                    sess.initiate(from, false);

                    sess.confId = iqElem.find('jingle').attr('confId');
                    sess.publisherId = iqElem.find('conference').attr("publisherId");
                    sess.replaceSid = iqElem.find('replaces').attr("sid");
                    sess.callShift = iqElem.find('replaces').attr("callShift") === "true";
                    sess.setRemoteDescription(jingleElem, 'offer').then(() => {
                        // The callback should be either ".sendAnswer and .accept" or .sendTerminate -- not necessarily synchronus
                        this.rxSubject.next({ name: 'jingleSessionInitiate', data: { sid: sess.sid } });
                    });

                    this.sessions[sess.sid] = sess;

                    // Handle dataChannel "x-rainbow-app" custom element
                    const applicationElem = iqElem.find('x-rainbow-app[xmlns="urn:xmpp:jingle:rainbow:media:desc"]');
                    if (applicationElem.length) {
                        const contentElem = applicationElem.find('content');
                        const id = contentElem.attr('id');
                        const description = contentElem.attr('description');
                        const affiliation = contentElem.attr('affiliation');
                        if (id) {
                            sess.application = { id };
                            if (description) sess.application.description = description;
                            if (affiliation) sess.application.affiliation = affiliation;
                        }
                    }

                    break;

                case 'session-accept':
                    this.logger.debug(`[StropheJinglePlugin] session-accept (sid: ${sid}, remoteType: ${sess.remoteType}, localType: ${sess.localType})`);
                    sess.setRemoteDescription(jingleElem, 'answer');
                    this.updateSessionData(sess, remoteType);
                    sess.state = 'active';
                    this.rxSubject.next({ name: 'jingleSessionAccept', data: { sid: sess.sid } });
                    break;

                case 'session-info':
                    if (iqElem.find('>jingle>ringing[xmlns="urn:xmpp:jingle:apps:rtp:info:1"]').length) {
                        this.logger.debug(`[StropheJinglePlugin] session-info (sid: ${sid}, action: ringing)`);
                        this.rxSubject.next({ name: 'jingleSessionInfoRinging', data: { sid: sess.sid } });
                    }
                    else if (iqElem.find('>jingle>iceFailed[xmlns="urn:xmpp:jingle:apps:rtp:info:1"]').length) {
                        this.logger.warn(`[StropheJinglePlugin] session-info (sid: ${sid}) -- icefailed`);
                    }
                    else if (iqElem.find('>jingle>hold[xmlns="urn:xmpp:jingle:apps:rtp:info:1"]').length) {
                        this.logger.debug(`[StropheJinglePlugin] session-info (sid: ${sid}) -- hold`);
                        this.rxSubject.next({ name: 'jingleSessionInfoHold', data: { sid: sess.sid } });
                    }
                    else if (iqElem.find('>jingle>unhold[xmlns="urn:xmpp:jingle:apps:rtp:info:1"]').length) {
                        this.logger.debug(`[StropheJinglePlugin] session-info (sid: ${sid}) -- unhold`);
                        this.rxSubject.next({ name: 'jingleSessionInfoUnhold', data: { sid: sess.sid } });
                    }
                    else if (iqElem.find('>jingle>refer[xmlns="urn:xmpp:jingle:apps:rtp:info:1"]').length) {
                        const referEleme = iqElem.find('>jingle>refer[xmlns="urn:xmpp:jingle:apps:rtp:info:1"]');
                        const status = referEleme.attr('status');
                        const confEndpointId = referEleme.attr('confEndpointId');
                        const referTo = referEleme.attr('referTo');
                        const method = referEleme.attr('method');
                        const otherSid = referEleme.attr('sid');
                        this.logger.debug(`[StropheJinglePlugin] session-info (sid: ${sid}) -- refer`);
                        this.rxSubject.next({ name: 'jingleSessionInfoRefer', data: { sid: sess.sid, status: status, confEndpointId: confEndpointId, referTo: referTo, method: method, otherSid } });
                    }
                    else if (iqElem.find('>jingle>slowlink[xmlns="urn:xmpp:jingle:apps:rtp:info:1"]').length) {
                        const slowlinkElem = iqElem.find('>jingle>slowlink[xmlns="urn:xmpp:jingle:apps:rtp:info:1"]');
                        const direction = slowlinkElem.attr('direction');
                        const media = slowlinkElem.attr('media');
                        const lost = slowlinkElem.attr('lost');
                        this.logger.debug(`[StropheJinglePlugin] session-info (sid: ${sid}) -- slowlink`);
                        this.rxSubject.next({ name: 'jingleSessionInfoSlowlink', data: { sid: sess.sid, direction: direction, media: media, lost: lost } });
                    }
                    else if (iqElem.find("renew").length) {
                        //we need to resend the current SDP with content-modify
                        sess.contentModify("audio");
                    }
                    break;

                case 'session-terminate':
                    this.logger.debug(`[StropheJinglePlugin] session-terminate (sid: ${sid})`);
                    sess.terminate();

                    if ($(iq).find('>jingle>conference').length && $(iq).find('>jingle>conference').attr('reason')) {
                        this.rxSubject.next({ name: 'jingleSessionTerminate', data: { sid: sess.sid, confId: sess.conference_id, reason: "multi-device" } });
                    }
                    else if ($(iq).find('>jingle>reason').length) {
                        this.rxSubject.next({ name: 'jingleSessionTerminate', data: { sid: sess.sid, confId: sess.conference_id, reason: $(iq).find('>jingle>reason>:first')[0].tagName, reasonText: $(iq).find('>jingle>reason>text').text() } });
                    }
                    else this.rxSubject.next({ name: 'jingleSessionTerminate', data: { sid: sess.sid, confId: sess.conference_id } });

                    //remove the session once we've send the events
                    this.terminate(sess.sid);
                    break;

                case 'transport-replace':
                    this.updateSessionData(sess, remoteType);
                    sess.isReconnecting = true;
                    this.sessions[sess.sid] = sess;

                    if (sess.hasSentTransportReplace) {
                        this.logger.debug(`[StropheJinglePlugin] transport-replace (sid: ${sid}) -- put on hold `);
                        if (!sess.isInitiator) {
                            //I'm not the initiator, then ACK the remote transport replace so that we can see what happens
                            this.connection.send(ack);
                        }
                        sess.saveIncomingTransportReplace(jingleElem, 'offer', ack);
                    }
                    else {
                        this.logger.debug(`[StropheJinglePlugin] transport-replace (sid: ${sid}) -- accepted`);
                        this.connection.send(ack);
                        sess.setRemoteDescription(jingleElem, 'offer').then(() => {
                            this.rxSubject.next({ name: 'jingleTransportReplace', data: { sid: sess.sid } });
                        });
                    }

                    break;

                case 'transport-accept':
                    this.logger.debug(`[StropheJinglePlugin] transport-accept (sid: ${sid}, remoteType: ${sess.remoteType}, localType: ${sess.localType})`);
                    sess.setRemoteDescription(jingleElem, 'answer');
                    this.updateSessionData(sess, remoteType);
                    sess.isReconnecting = false;
                    this.rxSubject.next({ name: 'jingleSessionAccept', data: { sid: sess.sid } });
                    break;

                case 'transport-info':
                    const contentElem = iqElem.find('>jingle>content');
                    sess.addIceCandidate(contentElem);
                    break;

                case 'content-add':
                    sess.remoteType = jingleElem.attr('mediaType');
                    this.logger.debug(`[StropheJinglePlugin] content-add (sid: ${sid}, remoteType: ${sess.remoteType}, localType: ${sess.localType})`);
                    sess.setRemoteDescription(jingleElem, 'offer').then(() => {
                        sess.mediaModified();
                    });

                    sess.isRenegotiating = true;
                    if ((iqElem.find('group').attr('semantics') === 'BUNDLE') && sess.conference_id && sess.conference_id !== -1) {
                        this.rxSubject.next({ name: 'jingleContentAdd', data: { sid: sess.sid, mediaType: sess.remoteType } });
                    }
                    break;

                case 'content-modify':
                    sess.remoteType = jingleElem.attr('mediaType');
                    this.logger.debug(`[StropheJinglePlugin] content-modify (sid: ${sid}, remoteType: ${sess.remoteType}, localType: ${sess.localType})`);
                    sess.setRemoteDescription(jingleElem, 'offer').then(() => {
                        sess.mediaModified();
                    });
                    sess.isRenegotiating = true;

                    if (($(iq).find('group').attr('semantics') === 'BUNDLE') && sess.conference_id && sess.conference_id !== -1) {
                        const medias = {};
                        const contentElems = jingleElem.find('content');
                        contentElems.each((__index, contentElem) => {
                            let name = $(contentElem).attr('name');
                            switch (name) {
                                case '0': name = 'audio'; break;
                                case '1': name = 'video'; break;
                                default: break;
                            }
                            medias[name] = $(contentElem).attr('senders') !== 'none';
                        });
                        this.connection.jingle?.rxSubject.next({ name: 'jingleContentModify', data: { sid: sess.sid, medias: medias } });
                    }
                    break;

                case 'content-remove':
                    sess.remoteType = jingleElem.attr('mediaType');
                    this.logger.debug(`[StropheJinglePlugin] content-remove (sid: ${sid}, remoteType: ${sess.remoteType}, localType: ${sess.localType})`);
                    sess.setRemoteDescription(jingleElem, 'offer').then(() => {
                        sess.mediaModified(true);
                    });
                    sess.isRenegotiating = true;
                    if (($(iq).find('group').attr('semantics') === 'BUNDLE') && sess.conference_id && sess.conference_id !== -1) {
                        this.rxSubject.next({ name: 'jingleContentRemove', data: { sid: sess.sid, mediaType: sess.remoteType } });
                    }
                    else {
                        this.rxSubject.next({ name: 'jingleContentRemove', data: { sid: sess.sid, mediaType: sess.remoteType } });
                    }
                    break;

                case 'content-accept':
                    this.logger.debug(`[StropheJinglePlugin] content-accept (sid: ${sid})`);
                    // In case of GW, we receive this after transport-replace, so we need to disable the reconnecting flag
                    sess.isReconnecting = false;
                    sess.setRemoteDescription(jingleElem, 'answer');
                    break;

                default:
                    this.logger.debug(`[StropheJinglePlugin] jingle action not implemented : ${action}`);
                    break;
            }

        } catch (error) {
            this.logger.error("[Strophe.jingle] On Jingle error catch: " + error);
            return true;
        }
        return true;
    }
}
